import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Offcanvas } from 'react-bootstrap';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { IndexerIcon } from './IndexerIcon';
import { GoogleSetupSectionsList } from './GoogleSetupSectionsList';
import { SETUP_VIDEO_URL } from '../lib/google-setup-guide';
import {
  getPageGuide,
  getTabGuide,
  type GuideBody,
  type PageGuideKey,
  type TabGuideKey,
} from '../lib/page-guides';

function GoogleSetupGuideBody() {
  return (
    <div>
      <div className="border rounded-3 overflow-hidden bg-body-tertiary mb-3">
        <video
          className="w-100 d-block"
          style={{ maxHeight: 420 }}
          src={SETUP_VIDEO_URL}
          controls
          playsInline
          preload="metadata"
        >
          مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.
        </video>
      </div>
      <GoogleSetupSectionsList />
    </div>
  );
}

export function SectionHelpBody({ guide }: { guide: GuideBody }) {
  return (
    <div className="text-end fs--1">
      {guide.paragraphs.map((paragraph) => (
        <p key={paragraph} className="mb-3 lh-lg">
          {paragraph}
        </p>
      ))}
      {guide.bullets && guide.bullets.length > 0 && (
        <ul className="text-secondary mb-3 ps-3">
          {guide.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      {guide.includeGoogleSetup && <GoogleSetupGuideBody />}
    </div>
  );
}

/** Question-mark control that opens a right-side help drawer. */
export function SectionHelpDrawer({
  title,
  guide,
  buttonClassName = 'section-help-btn',
  /** Use `span` when nested inside another button (e.g. workspace tabs). */
  triggerAs = 'button',
  children,
}: {
  title: string;
  guide: GuideBody;
  buttonClassName?: string;
  triggerAs?: 'button' | 'span';
  /** Optional label rendered before the help icon (e.g. page title text). */
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  function openHelp(event: SyntheticEvent) {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);
  }

  const trigger =
    triggerAs === 'span' ? (
      <span
        className={buttonClassName}
        role="button"
        tabIndex={0}
        aria-label={`راهنمای ${title}`}
        onClick={openHelp}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            openHelp(event);
          }
        }}
      >
        <IndexerIcon name="question-circle" />
      </span>
    ) : (
      <button
        type="button"
        className={buttonClassName}
        aria-label={`راهنمای ${title}`}
        onClick={openHelp}
      >
        <IndexerIcon name="question-circle" />
      </button>
    );

  return (
    <>
      <span className="d-inline-flex align-items-center gap-1 align-middle">
        {children}
        {trigger}
      </span>

      <Offcanvas
        show={open}
        onHide={() => setOpen(false)}
        placement="start"
        className="section-help-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fs--1 fw-semibold mb-0">{title}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <SectionHelpBody guide={guide} />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export function PageHelpTitle({
  guideKey,
  as: Tag = 'h1',
  className = 'h4 fw-bold mb-1',
}: {
  guideKey: PageGuideKey;
  as?: 'h1' | 'h2' | 'span';
  className?: string;
}) {
  const guide = getPageGuide(guideKey);
  return (
    <Tag className={className}>
      <SectionHelpDrawer title={guide.title} guide={guide}>
        <span>{guide.title}</span>
      </SectionHelpDrawer>
    </Tag>
  );
}

export function TabHelpButton({
  guideKey,
}: {
  guideKey: TabGuideKey;
}) {
  const guide = getTabGuide(guideKey);
  const html = (
    <>
      {guide.paragraphs.map((paragraph, index) => (
        <span key={paragraph}>
          {index > 0 ? (
            <>
              <br />
              <br />
            </>
          ) : null}
          {paragraph}
        </span>
      ))}
      {guide.bullets && guide.bullets.length > 0 ? (
        <>
          <br />
          <br />
          {guide.bullets.map((item) => (
            <span key={item}>
              • {item}
              <br />
            </span>
          ))}
        </>
      ) : null}
    </>
  );

  return <HelpTooltip html={html} placement="top" />;
}
